import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import appRoutes from '../../../../routing/app-routes';
import {
  clearVerificationPhoneNumberError,
  clearVerificationPhoneNumberFields,
  resetVerificationPhoneNumberRequestState,
  setPhoneNumber,
  setVerificationPhoneNumberCode,
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { verificationPhoneNumber } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { VerificationPhoneNumberError } from '../../../../store/features/user-access/user-registration/user-registration-types';
import { messagesForVerificationPhoneNumberError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';
import { PersonaTipoUtenteEnum } from '../../../../api';

export interface PhoneNumberVerificationPageLogicCallbacks {
  buttonDisabled: boolean;
  blockButton: boolean;
  openDialogAccordoQuadro: boolean;
  phoneNumber: string;
  verificationPhoneNumberCode: string;

  verificationPhoneNumberRequestStatus: ApiRequestStatus;
  verificationPhoneNumberError: VerificationPhoneNumberError;

  updateVerificationPhoneNumberCode: (code?: string) => void;
  handleClose: () => void;
  handleSubmit: () => void;
  handleCloseAccordoQuadroError: () => void;
}

export const usePhoneNumberVerificationPageLogic = (
  actions: GlobalActions
): PhoneNumberVerificationPageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const [openDialogAccordoQuadro, setOpenDialogAccordoQuadro] = useState(false)
  const [blockButton,setBlockButton] = useState(false)
  const {
    phoneNumber,
    verificationPhoneNumberCode,
    verificationPhoneNumberRequestStatus,
    verificationPhoneNumberError,
    selectedOptionUserType
  } = useAppSelector((state) => state.features.userRegistration);

  const [buttonDisabled, setButtonDisabled] = useState(
    !verificationPhoneNumberCode || verificationPhoneNumberCode?.length === 0
  );

  const updateVerificationPhoneNumberCode = useCallback(
    (value: string) => {
      if (buttonDisabled) {
        setButtonDisabled(false);
      }
      dispatch(setVerificationPhoneNumberCode(value));
    },
    [dispatch, buttonDisabled]
  );

  const handleSubmit = useCallback(() => {
    dispatch(verificationPhoneNumber());
  }, [dispatch]);

  useEffect(() => {
    if (verificationPhoneNumberRequestStatus === 'succeeded') {
      if (selectedOptionUserType === PersonaTipoUtenteEnum.Pf){
        navigate(appRoutes.InsertSecondaryPhoneNumberUserRegistration);
      }else {
        navigate(appRoutes.SignatureAreaUserRegistration);
      }
      dispatch(resetVerificationPhoneNumberRequestState());
    }
  }, [dispatch, navigate, verificationPhoneNumberRequestStatus]);

  useEffect(() => {
    if (verificationPhoneNumberError) {
      if(verificationPhoneNumberError?.type === 'TROPPI_TENTATIVI'){
        dispatch(setPhoneNumber(''))
      }
      if (verificationPhoneNumberError?.type === 'ACCORDO_QUADRO_NON_ATTIVO' && !blockButton){
        setBlockButton(true)
        setOpenDialogAccordoQuadro(true)
      }
      if (
        verificationPhoneNumberError?.type === 'GENERIC_ERROR' ||
        verificationPhoneNumberError?.type === 'CODICE_DI_VERIFICA_SBAGLIATO'
      ) {
        const errorMessages = messagesForVerificationPhoneNumberError(
          verificationPhoneNumberError,
          intl
        );
        showToast({
          type: 'error',
          title: errorMessages.title,
          description: errorMessages.description,
          close: closeToast,
        });
        dispatch(clearVerificationPhoneNumberError());
      }
    }
  }, [
    intl,
    verificationPhoneNumberError,
    dispatch,
    formatMessage,
    showToast,
    closeToast,
    blockButton,

  ]);

  const handleClose = useCallback(() => {
    dispatch(resetVerificationPhoneNumberRequestState());
    dispatch(clearVerificationPhoneNumberFields());
    navigate(appRoutes.InsertPhoneNumberUserRegistration, { replace: true });
  }, [dispatch, navigate]);

  const handleCloseAccordoQuadroError = ()=>{
    setOpenDialogAccordoQuadro(false)
  }
  return {
    buttonDisabled,
    phoneNumber,
    verificationPhoneNumberCode,
    verificationPhoneNumberRequestStatus,
    verificationPhoneNumberError,
    updateVerificationPhoneNumberCode,
    handleClose,
    handleSubmit,
    openDialogAccordoQuadro,
    blockButton,
    handleCloseAccordoQuadroError,
  };
};
